/**
 * Per-route document metadata for the public pages (audit: every route shipped
 * the same title/description). SPA without SSR: crawlers that execute JS and
 * browsers (tab titles, bookmarks, password managers) see per-route values;
 * the static index.html keeps the landing defaults for everything else.
 *
 * T-L01 added `canonicalPath`. The static index.html declares
 * `<link rel="canonical" href="https://pairpen.ru/">`, which every route inherited — so /offer,
 * /privacy and the rest each told crawlers "the real page is the home page". For legal documents
 * that is worse than an SEO defect: a payment provider or a regulator following the canonical link
 * lands on the landing page instead of the contract they were sent to read.
 */
import { useEffect } from 'react';

export const SITE_ORIGIN = 'https://pairpen.ru';
export const DEFAULT_PAGE_TITLE = 'PairPen — студия написания книг с AI';
export const DEFAULT_PAGE_DESCRIPTION =
  'PairPen — студия для написания книг с AI-ассистентом: главы и сцены, персонажи, постраничная вёрстка и экспорт в PDF.';

export interface PageMeta {
  title?: string;
  description?: string;
  /** Absolute path, e.g. `/offer`. Omitted on app routes, which stay canonical to the home page. */
  canonicalPath?: string;
}

function setMeta(selector: string, attribute: string, value: string): void {
  const node = document.querySelector(selector);
  if (node) {
    node.setAttribute(attribute, value);
  }
}

export function applyPageMeta({ title, description, canonicalPath }: PageMeta = {}): void {
  const resolvedTitle = title ?? DEFAULT_PAGE_TITLE;
  const resolvedDescription = description ?? DEFAULT_PAGE_DESCRIPTION;
  const url = `${SITE_ORIGIN}${canonicalPath ?? '/'}`;

  document.title = resolvedTitle;
  setMeta('meta[name="description"]', 'content', resolvedDescription);

  // Open Graph mirrors the same three values; leaving it on the landing copy meant every shared
  // link previewed as the home page.
  setMeta('meta[property="og:title"]', 'content', resolvedTitle);
  setMeta('meta[property="og:description"]', 'content', resolvedDescription);
  setMeta('meta[property="og:url"]', 'content', url);
  setMeta('link[rel="canonical"]', 'href', url);
}

export function usePageMeta({ title, description, canonicalPath }: PageMeta = {}): void {
  useEffect(() => {
    applyPageMeta({ title, description, canonicalPath });
  }, [title, description, canonicalPath]);
}
